import { useMemo, useState } from 'react'
import { ArrowRightIcon, CheckCheckIcon, ExternalLinkIcon, LoaderCircleIcon } from 'lucide-react'
import { observable, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { Checkbox, Field, FieldDescription, FieldError, FieldLabel, Input } from '@campai/lib-web/components/new/ui'
import { env } from '@campai/lib-web/env'
import { ValidationErrors } from '@campai/lib-web/utils/misc'
import { client, isTRPCClientError } from '@campai/lib-web/utils/trpc'

import { appState } from '@/appState'

import { Button } from './Button'
import { CreateOrganizationInput, Step } from './types'

const isWebsiteValid = (value: string) => {
    try {
        new URL(value)
        return true
    } catch {
        return false
    }
}

const isFinishStepValid = (input: CreateOrganizationInput) => {
    const hasWebsite = input.ui.noWebsite || isWebsiteValid(input.organization.website)
    const hasMemberSize = Number.isInteger(input.organization.memberCount) && input.organization.memberCount > 3

    return hasWebsite && hasMemberSize
}

const Element = observer(({ input }: { input: CreateOrganizationInput }) => {
    const { t } = useTranslation()
    const i18nKey = 'createOrganization.finish'
    const { ui } = input
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [createdOrganization, setCreatedOrganization] = useState<null | {
        name: string
        shortId: string
    }>(null)
    const organizationName = input.organization.name.trim() || t('createOrganization.welcome.namePlaceholder')
    const hasWebsiteError =
        !input.ui.noWebsite && input.organization.website.length > 0 && !isWebsiteValid(input.organization.website)
    const isValid = isFinishStepValid(input)
    const organizationUrl = createdOrganization ? `${env.VITE_PRODUCT_URL_ADMIN}/${createdOrganization.shortId}` : null
    const confettiPieces = useMemo(
        () =>
            new Array(24).fill(null).map((_, index) => ({
                id: index,
                left: 4 + ((index * 4.15) % 92),
                duration: 3.2 + (index % 5) * 0.45,
                delay: (index % 6) * 0.18,
                rotate: -24 + (index % 7) * 8,
                colorClassName: ['bg-sky-400', 'bg-emerald-400', 'bg-amber-300', 'bg-fuchsia-300', 'bg-rose-300'][
                    index % 5
                ],
            })),
        []
    )

    const handleSubmit = async () => {
        if (!isValid || isSubmitting || createdOrganization) {
            return
        }

        const { classification } = input.organization
        const website = input.ui.noWebsite ? '' : input.organization.website.trim()

        if (!classification.type || !classification.area) {
            return
        }

        setIsSubmitting(true)
        setHasError(false)
        runInAction(() => {
            ui.finishState = 'creating'
            ui.validationErrors = null
        })

        try {
            const { _id: organizationId } = await client.organizations.createOrganization.mutate({
                organization: {
                    ...input.organization,
                    website,
                    classification: {
                        ...classification,
                        type: classification.type,
                        area: classification.area,
                    },
                },
                finances: input.finances,
            })

            const organization = await client.organizations.getOrganization.query({
                organizationId: organizationId.toString(),
            })

            await appState.cache.removeFromStorage({ group: 'user', key: 'organizations' })
            setCreatedOrganization({
                name: organization.name,
                shortId: organization.shortId,
            })
            setIsSubmitting(false)
            runInAction(() => {
                ui.finishState = 'success'
            })
        } catch (error) {
            console.error(error)
            const validationErrors =
                isTRPCClientError(error) && error.data?.validation ? new ValidationErrors(error.data.validation) : null
            setHasError(true)
            setIsSubmitting(false)
            runInAction(() => {
                ui.finishState = 'idle'
                ui.validationErrors = validationErrors
            })
        }
    }

    return (
        <div className="relative">
            <style>{`
                @keyframes create-organization-confetti-fall {
                    0% {
                        transform: translate3d(0, -10vh, 0) rotate(0deg);
                        opacity: 0;
                    }
                    10% {
                        opacity: 1;
                    }
                    100% {
                        transform: translate3d(0, 115vh, 0) rotate(540deg);
                        opacity: 0;
                    }
                }
            `}</style>
            {createdOrganization && organizationUrl ? (
                <div className="flex items-center justify-center overflow-hidden px-6 py-8">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        {confettiPieces.map((piece) => (
                            <div
                                key={piece.id}
                                className={`absolute -top-10 h-4 w-2 rounded-full ${piece.colorClassName}`}
                                style={{
                                    left: `${piece.left}%`,
                                    animation: `create-organization-confetti-fall ${piece.duration}s linear ${piece.delay}s infinite`,
                                    transform: `rotate(${piece.rotate}deg)`,
                                }}
                            />
                        ))}
                    </div>

                    <div className="relative z-[1] mx-auto flex max-w-xl flex-col items-center px-8 py-10 text-center">
                        <div className="text-[3.5rem] leading-none">🎉</div>
                        <div className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-neutral-950">
                            {t(`${i18nKey}.success.title`)}
                        </div>
                        <div className="mt-2 text-lg font-medium text-neutral-950">{createdOrganization.name}</div>
                        <div className="mt-3 max-w-md text-base leading-7 text-neutral-600">
                            {t(`${i18nKey}.success.description`)}
                        </div>
                        <a
                            href={organizationUrl}
                            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-neutral-950 px-6 py-3 font-medium text-white shadow-[0_16px_32px_rgba(15,23,42,0.2)] transition-transform duration-200 hover:-translate-y-0.5"
                        >
                            <span>{t(`${i18nKey}.success.link`)}</span>
                            <ExternalLinkIcon className="size-4" />
                        </a>
                    </div>
                </div>
            ) : (
                <>
                    <div className="space-y-4 text-center">
                        <div className="mx-auto flex size-16 items-center justify-center rounded-[24px] bg-white/75 text-neutral-950 shadow-[0_10px_30px_rgba(255,255,255,0.22)]">
                            <CheckCheckIcon className="size-8" />
                        </div>
                        <div className="text-2xl font-semibold tracking-[-0.03em] text-neutral-950">
                            {t(`${i18nKey}.title`, { organizationName })}
                        </div>
                        <div className="mx-auto max-w-2xl text-base leading-7 text-neutral-600">
                            {t(`${i18nKey}.description`, { organizationName })}
                        </div>
                        <div className="mx-auto max-w-xl rounded-xl border border-white/20 bg-white/20 px-5 py-4 text-base leading-7 text-neutral-700">
                            {t(`${i18nKey}.trial`)}
                        </div>
                    </div>

                    <div className="mx-auto mt-8 max-w-xl space-y-6">
                        <Field className="min-w-0 flex-1">
                            <FieldLabel htmlFor="create-organization-member-size" required>
                                {t(`${i18nKey}.memberSize.label`)}
                            </FieldLabel>
                            <FieldDescription className="text-neutral-600">
                                {t(`${i18nKey}.memberSize.description`)}
                            </FieldDescription>
                            <Input
                                id="create-organization-member-size"
                                type="number"
                                min={100}
                                step={1}
                                aria-invalid={!!ui.validationErrors?.getError('organization.memberCount')}
                                placeholder="250"
                                value={input.organization.memberCount > 0 ? `${input.organization.memberCount}` : ''}
                                onChange={(event) =>
                                    runInAction(() => {
                                        const value = event.target.value
                                        input.organization.memberCount =
                                            value === '' ? 0 : Math.max(0, Number.parseInt(value, 10) || 0)
                                    })
                                }
                            />
                            <FieldError>{ui.validationErrors?.getError('organization.memberCount')}</FieldError>
                        </Field>

                        <div className="min-w-0 flex-1 space-y-3">
                            <Field>
                                <FieldLabel htmlFor="create-organization-website" required={!input.ui.noWebsite}>
                                    {t(`${i18nKey}.website.label`)}
                                </FieldLabel>
                                <FieldDescription className="text-neutral-600">
                                    {t(`${i18nKey}.website.description`)}
                                </FieldDescription>
                                <Input
                                    id="create-organization-website"
                                    type="url"
                                    placeholder="https://example.org"
                                    disabled={input.ui.noWebsite}
                                    className="disabled:opacity-60"
                                    aria-invalid={
                                        hasWebsiteError || !!ui.validationErrors?.getError('organization.website')
                                    }
                                    value={input.organization.website}
                                    onChange={(event) =>
                                        runInAction(() => {
                                            input.organization.website = event.target.value
                                        })
                                    }
                                />
                                <FieldError>
                                    {ui.validationErrors?.getError('organization.website') ||
                                        (hasWebsiteError ? t(`${i18nKey}.website.error`) : '')}
                                </FieldError>
                            </Field>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    checked={input.ui.noWebsite}
                                    onCheckedChange={(checked) =>
                                        runInAction(() => {
                                            input.ui.noWebsite = checked

                                            if (checked) {
                                                input.organization.website = ''
                                            }
                                        })
                                    }
                                />
                                <div>
                                    <div className="font-medium text-neutral-950">
                                        {t(`${i18nKey}.website.noWebsite`)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {hasError && (
                        <div className="mt-5 rounded-xl border border-red-300/70 bg-red-50/90 px-4 py-3 text-sm leading-6 text-red-950">
                            {t(`${i18nKey}.error`)}
                        </div>
                    )}

                    <div className="mt-8 flex justify-center">
                        <Button
                            disabled={isSubmitting || !isValid}
                            icon={
                                isSubmitting ? (
                                    <LoaderCircleIcon className="size-4 animate-spin" />
                                ) : (
                                    <ArrowRightIcon className="size-4" />
                                )
                            }
                            mode="label"
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? t(`${i18nKey}.creating`) : t(`${i18nKey}.submit`)}
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
})

export const createFinish = (input: CreateOrganizationInput): Step =>
    observable({
        id: 'finish',
        Element: () => <Element input={input} />,
        get isAvailable() {
            return true
        },
        get isValid() {
            return isFinishStepValid(input)
        },
    })
